import { DataConverter } from "Global/Base/Converters/DataConverter";
import { ROLELESS_ROLE_ID } from "Global/Base/Rights/constants";
import { IItemRights, RightLevel, RightType } from "../../../Global/Base/Rights/RightTypes";
import { IRoleImplicitRights, IRolesDefaultRightsResult } from "../../../OW.Remoting/Remoting/Rights";
import { parseRequiredRightElementAttribute, parseRightElementAttribute } from "./Utils";
/**
 * Práva nadefinována pro formulář
 */
export interface IFormRight {
    /**
    * Úroveň práv, ze kterého se nastavena podmínka
    */
    readonly level: number;
    readonly roleID: string | undefined;

    readonly read: boolean;
    readonly insert: boolean;
    readonly edit: boolean;
    readonly delete: boolean;

    readonly readConditionID: string | undefined;
    readonly insertConditionID: string | undefined;
    readonly editConditionID: string | undefined;
    readonly deleteConditionID: string | undefined;

    readonly isReadConditionNegative: boolean;
    readonly isInsertConditionNegative: boolean;
    readonly isEditConditionNegative: boolean;
    readonly isDeleteConditionNegative: boolean;

    /**
    * Vrací definici práva vkládání
    */
    getInsertRight(): IItemRights;

    /**
    * Vrací definici práva čtení
    */
    getReadRight(): IItemRights;

    /**
    * Vrací definici práva editace
    */
    getEditRight(): IItemRights;

    /**
    * Vrací definici práva mazání
    */
    getDeleteRight(): IItemRights;

    /**
     * Vrací implicitní hodnotu práva prvků
     * @param rightType zjišťované právo
     */
    getItemImplicitRight(rightType: RightType): IItemRights;
}
type ItemRightElementKeys = "deleteconditionid" |
    "delete" |
    "editconditionid" |
    "isdeleteconditionnegative" |
    "edit" |
    "isinsertconditionnegative" |
    "iseditconditionnegative" |
    "insertconditionid" |
    "insert" |
    "isreadconditionnegative" |
    "read" |
    "readconditionid" |
    "level" |
    "roleid";
/**
 * Práva nadefinována na komponentě z XGenu
 */
class FormRight implements IFormRight {
    /**
    * Úroveň aplikace, ze kterého se nastavena podmínka
    */
    public readonly level: number;
    public readonly roleID: string | undefined;

    public readonly read: boolean;
    public readonly insert: boolean;
    public readonly edit: boolean;
    public readonly delete: boolean;

    public readonly readConditionID: string | undefined;
    public readonly insertConditionID: string | undefined;
    public readonly editConditionID: string | undefined;
    public readonly deleteConditionID: string | undefined;

    public readonly isReadConditionNegative: boolean;
    public readonly isInsertConditionNegative: boolean;
    public readonly isEditConditionNegative: boolean;
    public readonly isDeleteConditionNegative: boolean;

    private _readRight: IItemRights | undefined = undefined;
    private _insertRight: IItemRights | undefined = undefined;
    private _editRight: IItemRights | undefined = undefined;
    private _deleteRight: IItemRights | undefined = undefined;

    constructor(newRight: HTMLElement, implicitItemsRolesRights: IRolesDefaultRightsResult) {
        this.level = DataConverter.toNumber(parseRequiredRightElementAttribute<ItemRightElementKeys>(newRight, "level"));
        this.roleID = parseRightElementAttribute<ItemRightElementKeys>(newRight, "roleid");

        //Read
        this.read = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "read"));
        this.readConditionID = parseRightElementAttribute<ItemRightElementKeys>(newRight, "readconditionid");
        this.isReadConditionNegative = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "isreadconditionnegative"));

        //Insert
        this.insert = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "insert"));
        this.insertConditionID = parseRightElementAttribute<ItemRightElementKeys>(newRight, "insertconditionid");
        this.isInsertConditionNegative = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "isinsertconditionnegative"));

        //Edit
        this.edit = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "edit"));
        this.editConditionID = parseRightElementAttribute<ItemRightElementKeys>(newRight, "editconditionid");
        this.isEditConditionNegative = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "iseditconditionnegative"));

        //Delete
        this.delete = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "delete"));
        this.deleteConditionID = parseRightElementAttribute<ItemRightElementKeys>(newRight, "deleteconditionid");
        this.isDeleteConditionNegative = DataConverter.toBoolean(parseRightElementAttribute<ItemRightElementKeys>(newRight, "isdeleteconditionnegative"));

        this.itemsImplicitRights = implicitItemsRolesRights.rights.find(item => item.id === this.roleID);
    }

    /*
     Implicitní práva prvků role, které patří daná definice práv
     */
    public readonly itemsImplicitRights: IRoleImplicitRights | undefined = undefined;

    private _createRight(roleID: string | undefined, firmRight: boolean, conditionID: string | undefined, isConditionNegative: boolean, level: number): IItemRights {
        return {
            roleID: roleID,
            value: firmRight,
            conditionID: conditionID,
            isNegative: isConditionNegative,
            level: level,
        };
    }

    /**
     * Vrací implicitní hodnotu práva prvků
     * @param rightType zjišťované právo
     */
    public getItemImplicitRight(rightType: RightType): IItemRights {
        let implicitValue;
        switch (rightType) {
            case RightType.visible:
                implicitValue = this.itemsImplicitRights?.visible;
                break;
            case RightType.enabled:
                implicitValue = this.itemsImplicitRights?.enabled;
                break;
            case RightType.required:
                implicitValue = this.itemsImplicitRights?.required;
                break;
            case RightType.postponedRequired:
                implicitValue = this.itemsImplicitRights?.postponedRequired;
                break;
            case RightType.recommended:
                implicitValue = this.itemsImplicitRights?.recommended;
                break;
            default:
                implicitValue = false;
        }
        return {
            roleID: this.roleID,
            value: implicitValue,
            conditionID: undefined,
            isNegative: false,
            level: this.roleID === ROLELESS_ROLE_ID ? RightLevel.allRolesFormLevel : RightLevel.formLevel,
        };
    }

    /**
    * Vrací definici práva vkládání
    */
    public getInsertRight(): IItemRights {
        if (!this._insertRight)
            this._insertRight = this._createRight(this.roleID, this.insert, this.insertConditionID, this.isInsertConditionNegative, this.level);
        return this._insertRight;
    }

    /**
    * Vrací definici práva čtení
    */
    public getReadRight(): IItemRights {
        if (!this._readRight)
            this._readRight = this._createRight(this.roleID, this.read, this.readConditionID, this.isReadConditionNegative, this.level);
        return this._readRight;
    }

    /**
    * Vrací definici práva editace
    */
    public getEditRight(): IItemRights {
        if (!this._editRight)
            this._editRight = this._createRight(this.roleID, this.edit, this.editConditionID, this.isEditConditionNegative, this.level);
        return this._editRight;
    }

    /**
    * Vrací definici práva mazání
    */
    public getDeleteRight(): IItemRights {
        if (!this._deleteRight)
            this._deleteRight = this._createRight(this.roleID, this.delete, this.deleteConditionID, this.isDeleteConditionNegative, this.level);
        return this._deleteRight;
    }
}

export class FormRightsExtractor {
    public static extractRightsFromElement(elementWithRightDefinition: HTMLElement, implicitItemsRolesRights: IRolesDefaultRightsResult): IFormRight {
        return new FormRight(elementWithRightDefinition, implicitItemsRolesRights);
    }
}
